import { NavLink, useNavigate } from 'react-router-dom';
import { Shield, Star, Zap, LogOut } from 'lucide-react';
import { NAV_ITEMS } from './navItems';
import EllieAvatar from '../EllieAvatar';
import { useAppStore } from '../../store/appStore';
import { useContentAccess } from '../../hooks/useContentAccess';

export default function Sidebar() {
  const navigate = useNavigate();
  const appMode = useAppStore((s) => s.appMode);
  const setAppMode = useAppStore((s) => s.setAppMode);
  const user = useAppStore((s) => s.user);
  const logout = useAppStore((s) => s.logout);
  const isParent = appMode.mode === 'parent';
  const { isPremium, isGuest } = useContentAccess();

  const handleLogout = () => {
    logout();
    navigate('/onboarding');
  };

  return (
    <aside className="hidden lg:flex flex-col w-56 shrink-0 bg-white border-r border-gray-100 fixed left-0 top-0 bottom-0 z-40">
      {/* Branding */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-gray-100">
        <EllieAvatar size={36} />
        <span className="font-display font-extrabold text-gray-800 text-lg leading-tight">
          KidWise
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map(({ to, label, Icon, isPremiumGated }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                isActive
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  size={18}
                  className={isActive ? 'text-primary-600' : 'text-gray-400'}
                />
                <span className="flex-1">{label}</span>
                {isPremiumGated && !isPremium && (
                  <span className="text-[9px] font-bold bg-warm-100 text-warm-700 px-1.5 py-0.5 rounded-full leading-none">
                    ✨
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Upgrade CTA — shown for non-premium users */}
      {!isPremium && (
        <div className="px-4 pb-3">
          <button
            onClick={() => navigate(isGuest ? '/auth' : '/pricing')}
            className="w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-warm-400 to-amber-500 text-white py-2.5 rounded-xl text-xs font-bold shadow-sm hover:opacity-90 transition-opacity"
          >
            <Zap size={13} />
            {isGuest ? 'Create Free Account' : 'Upgrade to Premium'}
          </button>
        </div>
      )}

      {/* User info + logout */}
      {user && (
        <div className="px-4 pb-3 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs font-bold text-gray-700 truncate">{user.name}</p>
              <p className="text-[10px] text-gray-400 capitalize">{user.tier === 'guest' ? 'Guest' : user.tier === 'premium' ? '⭐ Premium' : 'Free'}</p>
            </div>
            <button
              onClick={handleLogout}
              title="Log out"
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Mode toggle */}
      <div className="px-4 pb-6 pt-3 border-t border-gray-100">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">
          Mode
        </p>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => setAppMode('parent')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
              isParent ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            aria-pressed={isParent}
          >
            <Shield size={12} />
            Parent
          </button>
          <button
            onClick={() => setAppMode('kid')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
              !isParent ? 'bg-white text-warm-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            aria-pressed={!isParent}
          >
            <Star size={12} />
            Kid
          </button>
        </div>
      </div>
    </aside>
  );
}
